import { motion } from "framer-motion";
import { useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/hero-bar.jpg";

export function HeroSection() {
  // Pre-compute particle positions once to avoid forced reflows
  const particles = useMemo(() => {
    return [...Array(20)].map(() => ({
      x: Math.random() * 100, // Use percentage instead of pixels
      y: Math.random() * 100,
      scale: Math.random() * 0.5 + 0.5,
      yOffset: Math.random() * -200,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2,
    }));
  }, []);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Tommy Hour Bar Atmosphere"
          className="w-full h-full object-cover"
          fetchPriority="high"
          decoding="sync"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/50 via-transparent to-background/50" />
      </div>

      {/* Animated Particles/Lights Effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-primary/30"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
            }}
            initial={{
              scale: particle.scale,
              opacity: 0.3,
            }}
            animate={{
              y: [0, particle.yOffset],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              repeatType: "reverse",
              delay: particle.delay,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.p
            className="text-primary font-medium tracking-[0.3em] text-sm md:text-base mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            BANGKOK'S PREMIUM NIGHTLIFE
          </motion.p>

          <motion.h1
            className="font-display text-6xl md:text-8xl lg:text-9xl text-foreground mb-6 leading-none"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <span className="block">TOMMY</span>
            <span className="block text-gradient-gold">HOUR</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-foreground/70 mb-2 font-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Where the Night Begins
          </motion.p>

          <motion.p
            className="text-sm md:text-base text-muted-foreground mb-10 tracking-wider"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            Premium Cocktails • Late-Night Bar • Bangkok
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <Button
              variant="hero"
              size="xl"
              onClick={() => scrollToSection("#drinks")}
            >
              View Menu
            </Button>
            <Button
              variant="heroOutline"
              size="xl"
              onClick={() => scrollToSection("#reservation")}
            >
              Reserve a Table
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        <motion.button
          onClick={() => scrollToSection("#about")}
          className="text-foreground/50 hover:text-primary transition-colors"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          aria-label="Scroll to about section"
        >
          <ChevronDown className="w-8 h-8" aria-hidden="true" />
        </motion.button>
      </motion.div>
    </section>
  );
}
